import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../firebaseConfig';
import MailBody from '../InboxView/components/MailBody';
import './EmailHistoryPanel.css';

/**
 * EmailHistoryPanel — collapsible conversation history for the contact the
 * Normal-/Bulk-Mode editor is currently composing to.
 *
 * Solves the "blind follow-up" problem: when the user sends a follow-up they
 * previously could not see what the very first mail to this contact looked
 * like. This panel pulls every outbound mail (world_sent, matched by leadId or
 * recipient address) plus inbound replies (world_inbox) and lists them oldest
 * first, so the original outreach sits right at the top, one click away.
 *
 * Renders nothing when there is no prior history (e.g. a fresh cold email), so
 * it never clutters the first-touch flow.
 */

const toMillis = (value) => {
  if (value == null) return 0;
  if (typeof value === 'object' && typeof value.toMillis === 'function') return value.toMillis();
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const formatDate = (ms) => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
};

const formatDateLong = (ms) => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString('de-DE', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
};

const cleanAddress = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

export default function EmailHistoryPanel({ userId, contactId, recipientEmails = [] }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Stable key for the address list so the effect doesn't re-run on every render.
  const addrKey = (recipientEmails || [])
    .map((e) => (e || '').trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join(',');

  const reqRef = useRef(0);

  const fetchHistory = useCallback(async () => {
    if (!userId || (!contactId && !addrKey)) {
      setEmails([]);
      setLoading(false);
      return;
    }
    const reqId = ++reqRef.current;
    setLoading(true);

    const addresses = addrKey ? addrKey.split(',') : [];
    const seen = new Set();
    const collected = [];

    const pushDoc = (d, direction) => {
      const key = `${direction}:${d.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      const data = d.data();
      collected.push({
        id: d.id,
        ...data,
        _direction: direction,
        _ms: toMillis(data.timestamp || data.sentAt || data.date || data.createdAt),
      });
    };

    try {
      const sentRef = collection(firestore, `world_sent/${userId}/emails`);

      if (contactId) {
        try {
          const snap = await getDocs(query(sentRef, where('leadId', '==', contactId)));
          snap.docs.forEach((d) => pushDoc(d, 'sent'));
        } catch (_) {}
      }

      for (const addr of addresses) {
        try {
          const snap = await getDocs(query(sentRef, where('to', '==', addr)));
          snap.docs.forEach((d) => pushDoc(d, 'sent'));
        } catch (_) {}
      }
    } catch (err) {
      console.warn('[EmailHistoryPanel] load failed:', err?.message);
    }

    if (reqId !== reqRef.current) return;

    collected.sort((a, b) => a._ms - b._ms);
    setEmails(collected);
    setSelectedId(collected.length > 0 ? collected[0].id : null);
    setLoading(false);
  }, [userId, contactId, addrKey]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Close on outside click / Escape while open.
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Nothing to show → stay out of the way entirely.
  if (loading || emails.length === 0) return null;

  const sentCount = emails.filter((e) => e._direction === 'sent').length;
  const hasList = emails.length > 1;
  const selected = emails.find((m) => m.id === selectedId) || emails[0];
  const selectedIdx = emails.findIndex((m) => m.id === selected.id);

  return (
    <div className={`ehp-wrap ${open ? 'open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`ehp-trigger ${open ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        title="Bereits gesendete Mails an diesen Kontakt"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        {sentCount > 0 && <span className="ehp-trigger-count">{sentCount}</span>}
      </button>

      {open && (
        <div className={`ehp-dropdown ${hasList ? 'with-list' : ''}`}>
          <div className="ehp-dropdown-head">
            <div className="ehp-dropdown-title-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <span className="ehp-dropdown-title">Bereits gesendet</span>
            </div>
            <span className="ehp-dropdown-sub">{sentCount} {sentCount === 1 ? 'Mail' : 'Mails'}</span>
            <button type="button" className="ehp-close" onClick={() => setOpen(false)} title="Schließen">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="ehp-content">
            {hasList && (
              <div className="ehp-rail">
                {emails.map((mail, idx) => {
                  const isReceived = mail._direction === 'received';
                  const isFirst = idx === 0 && mail._direction === 'sent';
                  return (
                    <button
                      key={mail.id}
                      type="button"
                      className={`ehp-rail-item ${mail.id === selected.id ? 'active' : ''} ${isReceived ? 'received' : 'sent'}`}
                      onClick={() => setSelectedId(mail.id)}
                    >
                      <span className="ehp-rail-top">
                        <span className={`ehp-dir ${isReceived ? 'received' : 'sent'}`}>
                          {isReceived ? 'Antwort' : 'Gesendet'}
                        </span>
                        {isFirst && <span className="ehp-first-badge">1.</span>}
                      </span>
                      <span className="ehp-rail-subject">{mail.subject || '(kein Betreff)'}</span>
                      <span className="ehp-rail-date">{formatDate(mail._ms)}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="ehp-reader">
              <div className="ehp-reader-head">
                <div className="ehp-reader-subject">
                  {selectedIdx === 0 && selected._direction === 'sent' && (
                    <span className="ehp-first-badge">1. Mail</span>
                  )}
                  <span>{selected.subject || '(kein Betreff)'}</span>
                </div>
                <div className="ehp-meta">
                  <div className="ehp-meta-row">
                    <span className="ehp-meta-label">Von</span>
                    <span className="ehp-meta-value">{cleanAddress(selected.from) || '—'}</span>
                  </div>
                  <div className="ehp-meta-row">
                    <span className="ehp-meta-label">An</span>
                    <span className="ehp-meta-value">{cleanAddress(selected.to) || '—'}</span>
                  </div>
                  <div className="ehp-meta-row">
                    <span className="ehp-meta-label">Datum</span>
                    <span className="ehp-meta-value">{formatDateLong(selected._ms)}</span>
                  </div>
                </div>
              </div>
              <div className="ehp-reader-body">
                <MailBody email={selected} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
